import { AnswerInfo, OptionObject } from "@zitelab/ionic-fhir-questionnaires";

import { DiaprofilPatient } from "./diaprofil-patient";
import { FlatpickrOptions } from "ng2-flatpickr";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import { _i18n_str } from "../../assets/i18n/non-static-ids";

export const DIAPROFIL_QUESTIONNAIRE_UUID =
  "89a5cac9-1937-424a-a12d-9390b47ad68f";

export const CFD_DIAGNOSIS_TIMEPOINT_QUESTIONNAIRE_UUID =
  "9c7a3293-a8cd-427b-983b-6249727df22d";

export const KRAM_QUESTIONNAIRE_UUID = "cee1bdc3-c209-45ab-b448-97e0771ad837";

export const CONSULTATION_TOPICS_VALUESET_UUID =
  "7d78e5d1-2953-4168-af6b-8f2297e4aea1";

export const DIAPROFIL_ITEM_EVAL_QUESTIONNAIRE_UUID =
  "5afd0eff-2d20-4412-be27-c3de3a4695fe";

export type availableLanguages = "da-DK" | "en-GB";

export type UserTypes = "Practitioner" | "Patient" | "RelatedPerson";

export type Sex = "MALE" | "FEMALE";

export type DialogueInputType = "PATIENT_WISH" | "CONSULTATION_TOPIC";

export type RelevantSupportActionModalType = "PRO_ITEM" | DialogueInputType;

export interface ISupportFor {
  proItems: TopicItem[];
  patientWishes: OptionObject[];
  consultationTopics: OptionObject[];
  scoreIds: Score[];
  dashboardAreaIds?: topicNames[];
}

export interface IRelevantSupportActionModalNavParams {
  supportFor: ISupportFor;
  modalType: RelevantSupportActionModalType;
  patient: DiaprofilPatient;
  modalTitle: string;
  scoreResults?: { [key in Score]: IScoreResult };
}

export const sexOptions: {
  id: "MALE" | "FEMALE" | "ALL";
  i18n: string;
}[] = [
  {
    id: "ALL",
    i18n: _i18n_str("SEX_OPTIONS.ALL")
  },
  {
    id: "MALE",
    i18n: _i18n_str("SEX_OPTIONS.MALE")
  },
  {
    id: "FEMALE",
    i18n: _i18n_str("SEX_OPTIONS.FEMALE")
  }
];

export type UserRoles =
  | "PRACTITIONER"
  | "KIOSK_USER"
  | "LOCAL_ORGANIZATION_MANAGER"
  | "ADMIN";

export interface SubTaskObject {
  task: fhir.Task;
  procedureRequest: fhir.ProcedureRequest;
  questionnaire: fhir.Questionnaire;
  questionnaireResponse: fhir.QuestionnaireResponse;
  observations?: fhir.Observation[];
}

export interface FlattenedFhirState {
  [key: string]: SubTaskObject;
}

export type fhirValueTypes =
  | "valueBoolean"
  | "valueDecimal"
  | "valueInteger"
  | "valueDate"
  | "valueDateTime"
  | "valueTime"
  | "valueString"
  | "valueUri"
  | "valueAttachment"
  | "valueCoding"
  | "valueQuantity"
  | "valueReference"
  | "group"; // group is used when we have a repeating group

export type highlightColorNames = "RED" | "YELLOW" | "GREEN" | "NONE";

export interface HighlightColor {
  id: string;
  i18n: {
    singular: string;
    plural: string;
  };
  cssClass: string;
}

export const highlightColors: HighlightColor[] = [
  {
    id: "RED",
    i18n: {
      singular: _i18n_str("HIGHLIGHT_COLORS.RED.SINGULAR"),
      plural: _i18n_str("HIGHLIGHT_COLORS.RED.PLURAL")
    },
    cssClass: "dashboard-item-red"
  },
  {
    id: "YELLOW",
    i18n: {
      singular: _i18n_str("HIGHLIGHT_COLORS.YELLOW.SINGULAR"),
      plural: _i18n_str("HIGHLIGHT_COLORS.YELLOW.PLURAL")
    },
    cssClass: "dashboard-item-yellow"
  },
  {
    id: "GREEN",
    i18n: {
      singular: _i18n_str("HIGHLIGHT_COLORS.GREEN.SINGULAR"),
      plural: _i18n_str("HIGHLIGHT_COLORS.GREEN.PLURAL")
    },
    cssClass: "dashboard-item-green"
  },
  {
    id: "NONE",
    i18n: {
      singular: _i18n_str("HIGHLIGHT_COLORS.NONE.SINGULAR"),
      plural: _i18n_str("HIGHLIGHT_COLORS.NONE.PLURAL")
    },
    cssClass: "dashboard-item-none"
  }
];

export type topicNames =  // TODO: Rename to dashboardAreaNames
  | "HEALTH_AND_LIFE_STATUS"
  | "MENTAL_HEALTH"
  | "SYMPTOMS"
  | "DIABETES_IN_EVERYDAY_LIFE"
  | "DIABETES_WORRIES"
  | "SELF_CARE"
  | "BLOOD_SUGAR_REGULATION"
  | "IMPRESSION_OF_MEDICINE"
  | "CONTACT_TREATMENT_PROVIDER"
  | "WISHES" // WISHES is a legacy value, no longer used as of implementation of 1.0.8 qnr
  | "TOPIC_OF_CONVERSATION"
  | "MISC_IMPORTANT";

export interface Topic {
  // TODO: Rename to DashboardArea
  id: topicNames;
  i18n: string;
  topicType: string;
  freeTextFields: { [key: string]: string }; // Maps what fields are detailed free text fields for other fields
}

export const conversationTopic: Topic = {
  id: "TOPIC_OF_CONVERSATION",
  i18n: _i18n_str("VBS_PRO_DIA_TOPIC.TOPIC_OF_CONVERSATION"),
  topicType: "DIALOGUE",
  freeTextFields: {}
};

export const wishesLegacyTopic: Topic = {
  // Topic is no longer used since 1.0.8 version of questionnaire
  id: "WISHES",
  i18n: _i18n_str("VBS_PRO_DIA_TOPIC.WISHES"),
  topicType: "DIALOGUE",
  freeTextFields: {}
};

export const miscTopic: Topic = {
  id: "MISC_IMPORTANT",
  i18n: _i18n_str("VBS_PRO_DIA_TOPIC.MISC_IMPORTANT"),
  topicType: "MISC",
  freeTextFields: {}
};

export const dialogueTopics: Topic[] = [conversationTopic, wishesLegacyTopic];

export const careTopics: Topic[] = [
  {
    id: "HEALTH_AND_LIFE_STATUS",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.HEALTH_AND_LIFE_STATUS"),
    topicType: "NORMAL",
    freeTextFields: {
      problemsWorseningDiabetes: "problemsWorseningDiabetesDetails"
    }
  },
  {
    id: "MENTAL_HEALTH",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.MENTAL_HEALTH"),
    topicType: "SCORE",
    freeTextFields: {}
  },
  {
    id: "SYMPTOMS",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.SYMPTOMS"),
    topicType: "SYMPTOMS",
    freeTextFields: {
      otherDiseasesOrHealthIssues: "otherDiseasesOrHealthIssuesDetails"
    }
  },
  {
    id: "DIABETES_IN_EVERYDAY_LIFE",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.DIABETES_IN_EVERYDAY_LIFE"),
    topicType: "NORMAL",
    freeTextFields: {}
  },
  {
    id: "DIABETES_WORRIES",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.DIABETES_WORRIES"),
    topicType: "NORMAL",
    freeTextFields: {}
  },
  {
    id: "SELF_CARE",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.SELF_CARE"),
    topicType: "NORMAL",
    freeTextFields: {}
  },
  {
    id: "BLOOD_SUGAR_REGULATION",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.BLOOD_SUGAR_REGULATION"),
    topicType: "NORMAL",
    freeTextFields: {}
  },
  {
    id: "IMPRESSION_OF_MEDICINE",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.IMPRESSION_OF_MEDICINE"),
    topicType: "NORMAL",
    freeTextFields: {}
  },
  {
    id: "CONTACT_TREATMENT_PROVIDER",
    i18n: _i18n_str("VBS_PRO_DIA_TOPIC.CONTACT_TREATMENT_PROVIDER"),
    topicType: "NORMAL",
    freeTextFields: {}
  }
];

export interface TopicItem {
  config: WidgetConfig<any>;
  answerInfo: AnswerInfo;
  responseResource: fhir.QuestionnaireResponse;
  fullHistory: WidgetAnswerHistory;
  trendInfo: { prev: AnswerChange; next: AnswerChange };
}

export type DanishRegion =
  | "CAPITOL_REGION_OF_DENMARK"
  | "CENTRAL_DENMARK_REGION"
  | "NORTH_DENMARK_REGION"
  | "REGION_ZEALAND"
  | "REGION_OF_SOUTHERN_DENMARK";

export const danishRegions: { id: DanishRegion; i18n: string }[] = [
  {
    id: "CAPITOL_REGION_OF_DENMARK",
    i18n: _i18n_str("DANISH_REGIONS.CAPITOL_REGION_OF_DENMARK")
  },
  {
    id: "CENTRAL_DENMARK_REGION",
    i18n: _i18n_str("DANISH_REGIONS.CENTRAL_DENMARK_REGION")
  },
  {
    id: "NORTH_DENMARK_REGION",
    i18n: _i18n_str("DANISH_REGIONS.NORTH_DENMARK_REGION")
  },
  {
    id: "REGION_ZEALAND",
    i18n: _i18n_str("DANISH_REGIONS.REGION_ZEALAND")
  },
  {
    id: "REGION_OF_SOUTHERN_DENMARK",
    i18n: _i18n_str("DANISH_REGIONS.REGION_OF_SOUTHERN_DENMARK")
  }
];

export type DanishMunicipality =
  | "MORSOE"
  | "THISTED"
  | "BROENDERSLEV_DRONNINGLUND"
  | "FREDERIKSHAVN"
  | "VESTHIMMERLAND"
  | "LAESOE"
  | "REBILD"
  | "MARIAGERFJORD"
  | "JAMMERBUGT"
  | "AALBORG"
  | "HJOERRING"
  | "KOEBENHAVN"
  | "GULDBORGSUND"
  | "BROENDBY"
  | "ISHOEJ"
  | "HOEJE_TAASTRUP"
  | "OTHER";

export const danishMunicipalities: {
  id: DanishMunicipality;
  i18n: string;
}[] = [
  {
    id: "MORSOE",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.MORSOE")
  },
  {
    id: "THISTED",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.THISTED")
  },
  {
    id: "BROENDERSLEV_DRONNINGLUND",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.BROENDERSLEV_DRONNINGLUND")
  },
  {
    id: "FREDERIKSHAVN",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.FREDERIKSHAVN")
  },
  {
    id: "VESTHIMMERLAND",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.VESTHIMMERLAND")
  },
  {
    id: "LAESOE",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.LAESOE")
  },
  {
    id: "REBILD",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.REBILD")
  },
  {
    id: "MARIAGERFJORD",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.MARIAGERFJORD")
  },
  {
    id: "JAMMERBUGT",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.JAMMERBUGT")
  },
  {
    id: "AALBORG",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.AALBORG")
  },
  {
    id: "HJOERRING",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.HJOERRING")
  },
  {
    id: "KOEBENHAVN",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.KOEBENHAVN")
  },
  {
    id: "GULDBORGSUND",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.GULDBORGSUND")
  },
  {
    id: "BROENDBY",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.BROENDBY")
  },
  {
    id: "ISHOEJ",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.ISHOEJ")
  },
  {
    id: "HOEJE_TAASTRUP",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.HOEJE_TAASTRUP")
  },
  {
    id: "OTHER",
    i18n: _i18n_str("DANISH_MUNICIPALITIES.OTHER")
  }
];

export type ActionType =
  | "DIALOGUE_AID"
  | "REFERRAL"
  | "INFORMATION_MATERIAL"
  | "OTHER";

export const actionTypes: {
  id: ActionType;
  i18n: string;
}[] = [
  {
    id: "DIALOGUE_AID",
    i18n: _i18n_str("ACTION_TYPES.DIALOGUE_AID")
  },
  {
    id: "REFERRAL",
    i18n: _i18n_str("ACTION_TYPES.REFERRAL")
  },
  {
    id: "INFORMATION_MATERIAL",
    i18n: _i18n_str("ACTION_TYPES.INFORMATION_MATERIAL")
  },
  {
    id: "OTHER",
    i18n: _i18n_str("ACTION_TYPES.OTHER")
  }
];

export type DiaprofilTopic =
  | "SELF_CARE_SUPPORT" // Støtte til egenomsorg
  | "PRACTICAL_SUPPORT" // Praktisk støtte
  | "COMMUNITIES_AND_SOCIAL_RELATIONS" // Social støtte
  | "MENTAL_WELLBEING" // Mental trivsel
  | "ECONOMY" // Økonomi
  | "LATE_COMPLICATIONS" // Senkomplikationer
  | "FEET" // Fødder
  | "GASTROINTESTINAL_ISSUES" // Mave-tarmproblemer
  | "CHEST_PAINS_AND_PALPITATIONS_AND_BREATLESSNESS" // Brystsmerter, hjertebanken eller åndenød
  | "SLEEPLESSNESS" // Søvnproblemer
  | "EYE_CHANGES" // Øjenforandring
  | "SEXUALITY" // Seksualitet
  | "BLOOD_SUGAR_REGULATION" // Blodsukkerregulering
  | "LOW_BLOOD_SUGAR" // Lavt blodsukker
  | "EXERCISE" // Bevægelse
  | "DIET" // Kost
  | "WEIGHT" // Vægt
  | "ALCOHOL" // Alkohol
  | "SMOKING" // Rygning
  | "PATIENT_EDUCATION" // Patientuddannelse
  | "INSULIN_PUMP_OR_SENSOR" // Insulinpumpe/sensor
  | "DIABETES_EQUIPMENT" // Diabetesudstyr
  | "HCP_CONSIDERATIONS" // Overvejelser til behandler
  | "MEDICINE_ADMINISTRATION" // Medicinadministration
  | "OTHER";

export const diaprofilTopics: {
  id: DiaprofilTopic;
  i18n: string;
}[] = [
  {
    id: "SELF_CARE_SUPPORT",
    i18n: _i18n_str("ACTION_TOPICS.SELF_CARE_SUPPORT")
  },
  {
    id: "PRACTICAL_SUPPORT",
    i18n: _i18n_str("ACTION_TOPICS.PRACTICAL_SUPPORT")
  },
  {
    id: "COMMUNITIES_AND_SOCIAL_RELATIONS",
    i18n: _i18n_str("ACTION_TOPICS.COMMUNITIES_AND_SOCIAL_RELATIONS")
  },
  {
    id: "MENTAL_WELLBEING",
    i18n: _i18n_str("ACTION_TOPICS.MENTAL_WELLBEING")
  },
  {
    id: "ECONOMY",
    i18n: _i18n_str("ACTION_TOPICS.ECONOMY")
  },
  {
    id: "LATE_COMPLICATIONS",
    i18n: _i18n_str("ACTION_TOPICS.LATE_COMPLICATIONS")
  },
  {
    id: "FEET",
    i18n: _i18n_str("ACTION_TOPICS.FEET")
  },
  {
    id: "GASTROINTESTINAL_ISSUES",
    i18n: _i18n_str("ACTION_TOPICS.GASTROINTESTINAL_ISSUES")
  },
  {
    id: "CHEST_PAINS_AND_PALPITATIONS_AND_BREATLESSNESS",
    i18n: _i18n_str(
      "ACTION_TOPICS.CHEST_PAINS_AND_PALPITATIONS_AND_BREATLESSNESS"
    )
  },
  {
    id: "SLEEPLESSNESS",
    i18n: _i18n_str("ACTION_TOPICS.SLEEPLESSNESS")
  },
  {
    id: "EYE_CHANGES",
    i18n: _i18n_str("ACTION_TOPICS.EYE_CHANGES")
  },
  {
    id: "SEXUALITY",
    i18n: _i18n_str("ACTION_TOPICS.SEXUALITY")
  },
  {
    id: "BLOOD_SUGAR_REGULATION",
    i18n: _i18n_str("ACTION_TOPICS.BLOOD_SUGAR_REGULATION")
  },
  {
    id: "LOW_BLOOD_SUGAR",
    i18n: _i18n_str("ACTION_TOPICS.LOW_BLOOD_SUGAR")
  },
  {
    id: "EXERCISE",
    i18n: _i18n_str("ACTION_TOPICS.EXERCISE")
  },
  {
    id: "DIET",
    i18n: _i18n_str("ACTION_TOPICS.DIET")
  },
  {
    id: "WEIGHT",
    i18n: _i18n_str("ACTION_TOPICS.WEIGHT")
  },
  {
    id: "ALCOHOL",
    i18n: _i18n_str("ACTION_TOPICS.ALCOHOL")
  },
  {
    id: "SMOKING",
    i18n: _i18n_str("ACTION_TOPICS.SMOKING")
  },
  {
    id: "PATIENT_EDUCATION",
    i18n: _i18n_str("ACTION_TOPICS.PATIENT_EDUCATION")
  },
  {
    id: "INSULIN_PUMP_OR_SENSOR",
    i18n: _i18n_str("ACTION_TOPICS.INSULIN_PUMP_OR_SENSOR")
  },
  {
    id: "DIABETES_EQUIPMENT",
    i18n: _i18n_str("ACTION_TOPICS.DIABETES_EQUIPMENT")
  },
  {
    id: "HCP_CONSIDERATIONS",
    i18n: _i18n_str("ACTION_TOPICS.HCP_CONSIDERATIONS")
  },
  {
    id: "MEDICINE_ADMINISTRATION",
    i18n: _i18n_str("ACTION_TOPICS.MEDICINE_ADMINISTRATION")
  },
  {
    id: "OTHER",
    i18n: _i18n_str("ACTION_TOPICS.OTHER")
  }
];

export type Score = "WHO-5" | "MDI-2";

export const scores: {
  id: Score;
  i18n: string;
}[] = [
  {
    id: "WHO-5",
    i18n: _i18n_str("SCORE.WHO-5")
  },
  {
    id: "MDI-2",
    i18n: _i18n_str("SCORE.MDI-2")
  }
];

export type ProviderLevel = "NATIONAL" | "REGIONAL" | "MUNICIPAL";

export const providerLevels: {
  id: ProviderLevel;
  i18n: string;
}[] = [
  {
    id: "NATIONAL",
    i18n: _i18n_str("PROVIDER_LEVELS.NATIONAL")
  },
  {
    id: "REGIONAL",
    i18n: _i18n_str("PROVIDER_LEVELS.REGIONAL")
  },
  {
    id: "MUNICIPAL",
    i18n: _i18n_str("PROVIDER_LEVELS.MUNICIPAL")
  }
];

export type SupportedDiabetesType = "ALL" | "TYPE_ONE" | "TYPE_TWO";

export const supportedDiabetesTypes: {
  id: SupportedDiabetesType;
  i18n: string;
}[] = [
  {
    id: "ALL",
    i18n: _i18n_str("SUPPORTED_DIABETES_TYPES.ALL")
  },
  {
    id: "TYPE_ONE",
    i18n: _i18n_str("SUPPORTED_DIABETES_TYPES.TYPE_ONE")
  },
  {
    id: "TYPE_TWO",
    i18n: _i18n_str("SUPPORTED_DIABETES_TYPES.TYPE_TWO")
  }
];

export type diabetesTypes =
  | "UNKNOWN"
  | "DIABETES_TYPE_1"
  | "DIABETES_TYPE_2"
  | "OTHER_DIABETES_TYPE";

export const diabetesTypeOptions: {
  id: diabetesTypes;
  i18n: string;
}[] = [
  {
    id: "UNKNOWN",
    i18n: _i18n_str("DIABETES_TYPES.UNKNOWN")
  },
  {
    id: "DIABETES_TYPE_1",
    i18n: _i18n_str("DIABETES_TYPES.DIABETES_TYPE_1")
  },
  {
    id: "DIABETES_TYPE_2",
    i18n: _i18n_str("DIABETES_TYPES.DIABETES_TYPE_2")
  },
  {
    id: "OTHER_DIABETES_TYPE",
    i18n: _i18n_str("DIABETES_TYPES.OTHER_DIABETES_TYPE")
  }
];

export type bloodSugarMedicineTypes =
  | "UNKNOWN"
  | "PILLS"
  | "FAST_ACTING_INJECTION_OF_INSULIN_AT_MEALS"
  | "SLOW_ACTING_INJECTION_OF_INSULIN"
  | "INSULIN_PUMP"
  | "INJECTION_OF_OTHER_BLOOD_SUGAR_LOWERING_MEDICINE"
  | "NO_MEDICAL_TREATMENT";

export const bloodSugarMedicineOptions: {
  id: bloodSugarMedicineTypes;
  i18n: string;
}[] = [
  {
    id: "UNKNOWN",
    i18n: _i18n_str("BLOOD_SUGAR_MEDICINE_TYPES.UNKNOWN")
  },
  {
    id: "PILLS",
    i18n: _i18n_str("BLOOD_SUGAR_MEDICINE_TYPES.PILLS")
  },
  {
    id: "FAST_ACTING_INJECTION_OF_INSULIN_AT_MEALS",
    i18n: _i18n_str(
      "BLOOD_SUGAR_MEDICINE_TYPES.FAST_ACTING_INJECTION_OF_INSULIN_AT_MEALS"
    )
  },
  {
    id: "SLOW_ACTING_INJECTION_OF_INSULIN",
    i18n: _i18n_str(
      "BLOOD_SUGAR_MEDICINE_TYPES.SLOW_ACTING_INJECTION_OF_INSULIN"
    )
  },
  {
    id: "INSULIN_PUMP",
    i18n: _i18n_str("BLOOD_SUGAR_MEDICINE_TYPES.INSULIN_PUMP")
  },
  {
    id: "INJECTION_OF_OTHER_BLOOD_SUGAR_LOWERING_MEDICINE",
    i18n: _i18n_str(
      "BLOOD_SUGAR_MEDICINE_TYPES.INJECTION_OF_OTHER_BLOOD_SUGAR_LOWERING_MEDICINE"
    )
  },
  {
    id: "NO_MEDICAL_TREATMENT",
    i18n: _i18n_str("BLOOD_SUGAR_MEDICINE_TYPES.NO_MEDICAL_TREATMENT")
  }
];

export type staffType = "HCP" | "ADMINISTRATIVE_STAFF";

export const staffTypeOptions: {
  id: staffType;
  i18n: string;
}[] = [
  {
    id: "HCP",
    i18n: _i18n_str("STAFF_TYPE.HCP")
  },
  {
    id: "ADMINISTRATIVE_STAFF",
    i18n: _i18n_str("STAFF_TYPE.ADMINISTRATIVE_STAFF")
  }
];

export interface IDiaprofilUser {
  email: string;
  fhir_id: string;
  fullname: string;
  home_path: string;
  language: string;
  mobile_number: string;
  cell_phone_number?: string;
  cs_unique_id?: string;
  organization_uid: string;
  social_security_number: string;
  user_type: UserTypes;
  username: string;
  bloodSugarMedicineTypes: bloodSugarMedicineTypes[];
  consentToSms: patientConsentToSmsType;
  user_resource: fhir.Patient | fhir.Practitioner | fhir.RelatedPerson;
  diagnosis: diabetesTypes;
  diagnosisYear: string;
  evalConsent: evalConsentType;
  evalConsentResponseUuid: string;
  municipality: DanishMunicipality;
  hcpInitials: string;
  isDeactivated: boolean;
  deactivationReason: string;
  deactivationTimestamp: string;
  staffType: staffType;
  evalStudyParticipantId: string;
  last_login_time: string;
  twofactor_authenticated_time: string;
  twofactor_enabled: boolean;
  twofactor_messaging_methods: string[];
}

export type EvaluationProjectType = "NONE" | "VBS_PRO_DIA" | "M_PRODIA";

export const evaluationProjectOptions: {
  id: EvaluationProjectType;
  i18n: string;
}[] = [
  {
    id: "NONE",
    i18n: _i18n_str("EVALUATION_PROJECT.NONE")
  },
  {
    id: "VBS_PRO_DIA",
    i18n: _i18n_str("EVALUATION_PROJECT.VBS_PRO_DIA")
  },
  {
    id: "M_PRODIA",
    i18n: _i18n_str("EVALUATION_PROJECT.M_PRODIA")
  }
];

export type EvaluationProjectStatus =
  | "NOT_IN_STUDY"
  | "NOT_BEGUN"
  | "ACTIVE"
  | "COMPLETED";

export const evaluationProjectStatusOptions: {
  id: EvaluationProjectStatus;
  i18n: string;
}[] = [
  {
    id: "NOT_IN_STUDY",
    i18n: _i18n_str("EVALUATION_PROJECT_STATUS.NOT_IN_STUDY")
  },
  {
    id: "NOT_BEGUN",
    i18n: _i18n_str("EVALUATION_PROJECT_STATUS.NOT_BEGUN")
  },
  {
    id: "ACTIVE",
    i18n: _i18n_str("EVALUATION_PROJECT_STATUS.ACTIVE")
  },
  {
    id: "COMPLETED",
    i18n: _i18n_str("EVALUATION_PROJECT_STATUS.COMPLETED")
  }
];

export type EvaluationConsentType = "NONE" | "NORMAL" | "STRICT";

export type FeatureFlagEnabledType = "YES" | "NO";

export type Hcp2FaType = "DISABLED" | "ENABLED";

export interface IDiaprofilOrganizationConfig {
  organization_resource: fhir.Organization;
  patient_support_name_of_support_person: string;
  patient_support_name_of_organization: string;
  patient_support_telephone_number: string;
  patient_support_email: string;
  patient_support_address: string;
  hcp_support_name_of_support_person: string;
  hcp_support_name_of_organization: string;
  hcp_support_telephone_number: string;
  hcp_support_email: string;
  hcp_support_address: string;
  patient_schema_medicine_types_default: bloodSugarMedicineTypes[];
  patient_schema_medicine_types_required: boolean;
  patient_schema_municipality_default: DanishMunicipality;
  patient_schema_municipality_required: boolean;
  patient_schema_diabetes_type_required: boolean;
  patient_schema_diabetes_type_default: diabetesTypes;
  patient_schema_diagnosis_year_required: boolean;
  patient_schema_consent_to_send_sms_enabled: boolean;
  notification_type: { title: string; token: PatientNotificationTypes };
  human_readable_notification_plan: string;
  eval_project: { title: string; token: EvaluationProjectType };
  eval_project_participation_status: {
    title: string;
    token: EvaluationProjectStatus;
  };
  eval_pt_consent_type: { title: string; token: EvaluationConsentType };
  eval_pt_consent_qnr: { token: string; title: string };
  eval_pt_post_pro_demo_qnr: { token: string; title: string };
  eval_pt_post_visit_qnr: { token: string; title: string };
  eval_hcp_post_visit_qnr: { token: string; title: string };
  eval_hcp_consent_qnr: { token: string; title: string };
  eval_hcp_bl_qnr: { token: string; title: string };
  eval_hcp_bl_extra_qnr: { token: string; title: string };
  eval_hcp_re_pt_demo_qnr: { token: string; title: string };
  eval_data_download_enabled: {
    title: string;
    token: FeatureEnabledVocab;
  };
  pro_task_available_days_before: number;
  ff_pro_task_consultation_type_extension_enabled: boolean;
  ff_pro_task_consultation_type_extension_default: {
    token: proTaskConsultationType;
    title: string;
  };
  ff_pro_task_consultation_type_extension_options: {
    token: proTaskConsultationType;
    title: string;
  }[];
  ff_default_columns_in_patient_listing: {
    token: PatientListingColumnType;
    title: string;
  }[];
  ff_duty_of_disclosure_page: boolean;
  organization_specific_note_to_patient: string;
  ff_hcp_users_page: { title: string; token: FeatureEnabledVocab };
  ff_statistics_page: { title: string; token: FeatureEnabledVocab };
  ff_diagnosis_timepoint_in_dashboard: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_hcp_pro_patch: { title: string; token: FeatureEnabledVocab };
  ff_patient_page_default_view: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_import_from_kih_modal: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_hcp_2fa_login: {
    title: string;
    token: Hcp2FaType;
  };
  ff_current_consultations_page: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_time_of_consultation_ext: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_soft_required_mobile_number: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_ssn_registry_integration: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_kram_btn_in_dashboard: {
    title: string;
    token: FeatureEnabledVocab;
  };
  ff_partial_consultation_completion: {
    title: string;
    token: FeatureEnabledVocab;
  };
  eboks_task_no_show_letter_template: Record<string, any>;
}

export type PatientListingColumnType =
  | "socialSecurityNumber"
  | "fullName"
  | "age"
  | "sex"
  | "diagnosis"
  | "isPumpUser"
  | "diagnosisYear"
  | "municipality"
  | "evalConsent"
  | "numberOfResponses"
  | "dateOfLatestResponse"
  | "latestWho5"
  | "dateOfActiveTask"
  | "evalStudyParticipantId";

export const patientListingColumnOptions: {
  id: PatientListingColumnType;
  i18n: string;
}[] = [
  {
    id: "socialSecurityNumber",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.SOCIAL_SECURITY_NUMBER")
  },
  {
    id: "fullName",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.FULL_NAME")
  },
  {
    id: "age",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.AGE")
  },
  {
    id: "sex",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.SEX")
  },
  {
    id: "diagnosis",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.DIABETES_TYPE")
  },
  {
    id: "isPumpUser",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.BLOOD_SUGAR_MEDICINE_TYPES")
  },
  {
    id: "diagnosisYear",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.DIAGNOSIS_YEAR")
  },
  {
    id: "evalConsent",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.EVALUATION_CONSENT")
  },
  {
    id: "municipality",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.MUNICIPALITY")
  },
  {
    id: "numberOfResponses",
    i18n: _i18n_str(
      "PATIENT_LISTING.TABLE.COLUMNS.NUMBER_OF_QUESTIONNAIRE_RESPONSES"
    )
  },
  {
    id: "dateOfLatestResponse",
    i18n: _i18n_str(
      "PATIENT_LISTING.TABLE.COLUMNS.LATEST_QUESTIONNAIRE_RESPONSE"
    )
  },
  {
    id: "latestWho5",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.LASTEST_WHO5")
  },
  {
    id: "dateOfActiveTask",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.ACTIVE_TASK")
  },
  {
    id: "evalStudyParticipantId",
    i18n: _i18n_str("PATIENT_LISTING.TABLE.COLUMNS.EVAL_STUDY_PARTICIPANT_ID")
  }
];

export type PatientNotificationTypes =
  | "NONE"
  | "EBOKS"
  | "SMS"
  | "EBOKS_AND_SMS";

export type manualItemTypes =
  | "DIAPROFIL_PRO_ITEM"
  | "CONSULTATION_TOPIC"
  | "SCORE"
  | "DASHBOARD_AREA";

export const manualItemTypeOptions: {
  id: manualItemTypes;
  i18n: string;
}[] = [
  {
    id: "DIAPROFIL_PRO_ITEM",
    i18n: _i18n_str("MANUAL_ITEM_LEVEL_TYPES.DIAPROFIL_PRO_ITEM")
  },
  {
    id: "CONSULTATION_TOPIC",
    i18n: _i18n_str("MANUAL_ITEM_LEVEL_TYPES.CONSULTATION_TOPIC")
  },
  {
    id: "SCORE",
    i18n: _i18n_str("MANUAL_ITEM_LEVEL_TYPES.SCORE")
  },
  {
    id: "DASHBOARD_AREA",
    i18n: _i18n_str("MANUAL_ITEM_LEVEL_TYPES.DASHBOARD_AREA")
  }
];

export interface PatientHistoryElement {
  responseResource: fhir.QuestionnaireResponse;
  items: { config: WidgetConfig<any>; answerInfo: AnswerInfo }[];
}

export type PatientHistory = PatientHistoryElement[];

export interface AnswerChange {
  responseId: string;
  date: string;
  answerInfo: AnswerInfo;
  changeCode: AnswerColorChangeCode;
  changeCodeIcon: string;
}

export type AnswerColorChangeCode =
  | "NOTHING_TO_COMPARE_WITH"
  | "CANNOT_CALCULATE"
  | "BETTER"
  | "SAME"
  | "WORSE";

export interface WidgetAnswerHistory {
  widget: WidgetConfig<any>;
  answers: {
    source: fhir.QuestionnaireResponse;
    answerInfo: AnswerInfo;
    change: {
      prev: AnswerChange;
      next: AnswerChange;
    };
  }[];
}

export const changeCodeMapping: {
  [key: string]: { [key: string]: AnswerColorChangeCode };
} = {
  "dashboard-item-green": {
    "dashboard-item-green": "SAME",
    "dashboard-item-yellow": "WORSE",
    "dashboard-item-red": "WORSE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-yellow": {
    "dashboard-item-green": "BETTER",
    "dashboard-item-yellow": "SAME",
    "dashboard-item-red": "WORSE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-red": {
    "dashboard-item-green": "BETTER",
    "dashboard-item-yellow": "BETTER",
    "dashboard-item-red": "SAME",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-none": {
    "dashboard-item-green": "CANNOT_CALCULATE",
    "dashboard-item-yellow": "CANNOT_CALCULATE",
    "dashboard-item-red": "CANNOT_CALCULATE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-not-answerable": {
    "dashboard-item-green": "CANNOT_CALCULATE",
    "dashboard-item-yellow": "CANNOT_CALCULATE",
    "dashboard-item-red": "CANNOT_CALCULATE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-no-answer": {
    "dashboard-item-green": "CANNOT_CALCULATE",
    "dashboard-item-yellow": "CANNOT_CALCULATE",
    "dashboard-item-red": "CANNOT_CALCULATE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  "dashboard-item-legacy-answer": {
    "dashboard-item-green": "CANNOT_CALCULATE",
    "dashboard-item-yellow": "CANNOT_CALCULATE",
    "dashboard-item-red": "CANNOT_CALCULATE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  },
  danger: {
    "dashboard-item-green": "CANNOT_CALCULATE",
    "dashboard-item-yellow": "CANNOT_CALCULATE",
    "dashboard-item-red": "CANNOT_CALCULATE",
    "dashboard-item-none": "CANNOT_CALCULATE",
    "dashboard-item-not-answerable": "CANNOT_CALCULATE",
    "dashboard-item-no-answer": "CANNOT_CALCULATE",
    "dashboard-item-legacy-answer": "CANNOT_CALCULATE",
    danger: "CANNOT_CALCULATE"
  }
};

export const changeCodeIcons: { [key in AnswerColorChangeCode]: string } = {
  BETTER: "trending-up",
  SAME: "remove",
  WORSE: "trending-down",
  CANNOT_CALCULATE: "information",
  NOTHING_TO_COMPARE_WITH: "information"
};

export type ItemEvaluationTopic =
  | "ISSUE_WITH_COLOR_CODE"
  | "OTHER_ISSUE"
  | "ISSUE_WITH_TREND_DISPLAY";

export const itemEvaluationTopics: {
  id: ItemEvaluationTopic;
  i18n: string;
}[] = [
  {
    id: "ISSUE_WITH_COLOR_CODE",
    i18n: _i18n_str("ITEM_EVALUATION_TOPICS.ISSUE_WITH_COLOR_CODE")
  },
  {
    id: "OTHER_ISSUE",
    i18n: _i18n_str("ITEM_EVALUATION_TOPICS.OTHER_ISSUE")
  },
  {
    id: "ISSUE_WITH_TREND_DISPLAY",
    i18n: _i18n_str("ITEM_EVALUATION_TOPICS.ISSUE_WITH_TREND_DISPLAY")
  }
];

export type ColorCodeSuggestion = "GREEN" | "YELLOW" | "RED" | "DONT_KNOW";

export const colorCodeSuggestionOptions: {
  id: ColorCodeSuggestion;
  i18n: string;
}[] = [
  {
    id: "GREEN",
    i18n: _i18n_str("COLOR_CODE_SUGGESTION_OPTIONS.GREEN")
  },
  {
    id: "YELLOW",
    i18n: _i18n_str("COLOR_CODE_SUGGESTION_OPTIONS.YELLOW")
  },
  {
    id: "RED",
    i18n: _i18n_str("COLOR_CODE_SUGGESTION_OPTIONS.RED")
  },
  {
    id: "DONT_KNOW",
    i18n: _i18n_str("COLOR_CODE_SUGGESTION_OPTIONS.DONT_KNOW")
  }
];

export type WrongColorReason =
  | "WRONG_ANSWER_BY_PATIENT"
  | "RIGHT_ANSWER_BUT_INCORRECT_COLOR_CODE";

export const wrongColorReasonOptions: {
  id: WrongColorReason;
  i18n: string;
}[] = [
  {
    id: "WRONG_ANSWER_BY_PATIENT",
    i18n: _i18n_str("WRONG_COLOR_REASON_OPTIONS.WRONG_ANSWER_BY_PATIENT")
  },
  {
    id: "RIGHT_ANSWER_BUT_INCORRECT_COLOR_CODE",
    i18n: _i18n_str(
      "WRONG_COLOR_REASON_OPTIONS.RIGHT_ANSWER_BUT_INCORRECT_COLOR_CODE"
    )
  }
];

export interface IDashboardItemEvaluation {
  id: string;
  questionnaireResponseId: string;
  topicId: string;
  questionnaireItemId: string;
  problemType: ItemEvaluationTopic;
  colorCodeIssueSuggestion: ColorCodeSuggestion;
  colorCodeIssueReason: WrongColorReason;
  issueText: string;
}

export interface IScoreResult {
  scoreId: Score;
  title: string;
  score: number;
  min: number;
  max: number;
  color: string;
  responseId?: string;
}

export type activeTaskStatus = "NORMAL" | "CLOSE_TO_EXPIRATION" | "EXPIRED";

export interface IDiaprofilHcp {
  fhirId: string;
  userName: string;
  fhirResource: fhir.Practitioner;
  userRole: string;
  email: string;
  fullName: string;
  csUniqueId: string;
  hcpInitials: string;
  homePath: string;
  mobileNumber: string;
  organizationUid: string;
  isDeactivated: boolean;
  deactivationReason: string;
  deactivationTimestamp: string;
  evalStudyParticipantId: string;
  lastLoginTime: string;
  staffType: staffType;
  twoFactorEnabled: boolean;
  twoFactorMethods: string[];
}

export interface IConsultationStatusDetails {
  id: string;
  handled: boolean;
  status: ConsultationStatus;
  hcpId: string;
  hcpInitials: string;
  dateTime: string;
  comment: string;
  btnColor: string;
  btnIcon: string;
  btnText: string;
}

export type patientConsentToSmsType =
  | "YES_CONSENT_GIVEN"
  | "NO_CONSENT_DENIED"
  | "NOT_ANSWERED";

export const patientConsentToSmsOptions: {
  id: patientConsentToSmsType;
  i18n: string;
}[] = [
  {
    id: "YES_CONSENT_GIVEN",
    i18n: _i18n_str("PATIENT_CONSENT_TO_SMS_OPTIONS.YES_CONSENT_GIVEN")
  },
  {
    id: "NO_CONSENT_DENIED",
    i18n: _i18n_str("PATIENT_CONSENT_TO_SMS_OPTIONS.NO_CONSENT_DENIED")
  },
  {
    id: "NOT_ANSWERED",
    i18n: _i18n_str("PATIENT_CONSENT_TO_SMS_OPTIONS.NOT_ANSWERED")
  }
];

export type evalConsentType =
  | "YES_I_CONSENT"
  | "NO_I_DO_NOT_CONSENT"
  | "NOT_ANSWERED";

export const evalConsentOptions: {
  id: evalConsentType;
  i18n: string;
}[] = [
  {
    id: "YES_I_CONSENT",
    i18n: _i18n_str("EVALUATION_CONSENT_OPTIONS.YES_I_CONSENT")
  },
  {
    id: "NO_I_DO_NOT_CONSENT",
    i18n: _i18n_str("EVALUATION_CONSENT_OPTIONS.NO_I_DO_NOT_CONSENT")
  },
  {
    id: "NOT_ANSWERED",
    i18n: _i18n_str("EVALUATION_CONSENT_OPTIONS.NOT_ANSWERED")
  }
];

export type proTaskConsultationType =
  | "CLARIFYING_CONVERSATION"
  | "COURSE_OF_ILLNESS_CONVERSATION"
  | "CONCLUDING_CONVERSATION"
  | "YEARLY_STATUS_WITH_NURSE"
  | "PUMP_STATUS_WITH_NURSE"
  | "PRELIMINARY_EXAMINATION_WITH_DOCTOR"
  | "THREE_MONTH_FOLLOW_UP"
  | "SAME_DAY_SCREENING"
  | "START_CONVERSATION"
  | "END_CONVERSATION"
  | "OTHER";

export const proTaskConsultationTypeOptions: {
  id: proTaskConsultationType;
  i18n: string;
}[] = [
  {
    id: "CLARIFYING_CONVERSATION",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.CLARIFYING_CONVERSATION")
  },
  {
    id: "COURSE_OF_ILLNESS_CONVERSATION",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.COURSE_OF_ILLNESS_CONVERSATION")
  },
  {
    id: "CONCLUDING_CONVERSATION",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.CONCLUDING_CONVERSATION")
  },
  {
    id: "YEARLY_STATUS_WITH_NURSE",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.YEARLY_STATUS_WITH_NURSE")
  },
  {
    id: "PUMP_STATUS_WITH_NURSE",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.PUMP_STATUS_WITH_NURSE")
  },
  {
    id: "PRELIMINARY_EXAMINATION_WITH_DOCTOR",
    i18n: _i18n_str(
      "PRO_TASK_CONSULTATION_TYPE.PRELIMINARY_EXAMINATION_WITH_DOCTOR"
    )
  },
  {
    id: "THREE_MONTH_FOLLOW_UP",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.THREE_MONTH_FOLLOW_UP")
  },
  {
    id: "START_CONVERSATION",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.START_CONVERSATION")
  },
  {
    id: "END_CONVERSATION",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.END_CONVERSATION")
  },
  {
    id: "SAME_DAY_SCREENING",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.SAME_DAY_SCREENING")
  },
  {
    id: "OTHER",
    i18n: _i18n_str("PRO_TASK_CONSULTATION_TYPE.OTHER")
  }
];

export const flatpickrConfig: FlatpickrOptions = {
  weekNumbers: true,
  minDate: "today",
  locale: {
    weekdays: {
      shorthand: ["søn", "man", "tir", "ons", "tors", "fre", "lør"],
      longhand: [
        "søndag",
        "mandag",
        "tirsdag",
        "onsdag",
        "torsdag",
        "fredag",
        "lørdag"
      ]
    },
    months: {
      shorthand: [
        "jan",
        "feb",
        "mar",
        "apr",
        "maj",
        "jun",
        "jul",
        "aug",
        "sep",
        "okt",
        "nov",
        "dec"
      ],
      longhand: [
        "januar",
        "februar",
        "marts",
        "april",
        "maj",
        "juni",
        "juli",
        "august",
        "september",
        "oktober",
        "november",
        "december"
      ]
    },
    ordinal: function() {
      return ".";
    },
    firstDayOfWeek: 1,
    rangeSeparator: " til ",
    weekAbbreviation: "uge",
    time_24hr: true,
    dateFormat: "d-m-Y",
    altFormat: "d-m-Y",
    altInput: true
  }
};

export const flatpickrTimepointConfig: FlatpickrOptions = {
  enableTime: true,
  noCalendar: true,
  time_24hr: true,
  dateFormat: "H:i",
  minTime: "06:00",
  maxTime: "22:00"
};

export const PRO_TASK_CONSULTATION_TYPE_EXT_URL =
  "http://zitelab.eu/fhir/extensions/diaprofil-pro-task-consultation-type";

export const PRO_TASK_CONSULTATION_TIME_EXT_URL =
  "http://zitelab.eu/fhir/extensions/diaprofil-pro-task-consultation-time";

export const PRO_TASK_QNR_VERSION_EXT_URL =
  "https://fhir.zitelab.eu/extensions/hestia-questionnaire-version";

export type FeatureEnabledVocab =
  | "DISABLED"
  | "ENABLED_FOR_SUPER_USERS"
  | "ENABLED";

export interface IUsageStatistics {
  pro: {
    totalQuestionnaireCount: number;
    patientsWithZero: number;
    patientsWithOne: number;
    patientsWithTwoOrMore: number;
    totalOnKiosk: number;
    totalFromHome: number;
  };
  evalStudy: {
    consentGiven: number;
    consentDenied: number;
    completedQuestionnaires: number;
    completedPatientPostProEval: number;
    completedPatientPostVisitEval: number;
    completedHcpPostVisitEval: number;
  };
}

export interface ITypewriterField {
  slug: string;
  type: "text" | "richText" | "date" | "selection";
  label: string;
  value: any;
}

export interface ITypewriterResult {
  project: string;
  type: "news" | "service-message";
  slug: string;
  title: string;
  fields: ITypewriterField[];
}

export interface IDiaprofilNews {
  id: string;
  header: string;
  content: string;
  date: string;
  time: string;
  display: "Yes" | "No";
}

export interface IDiaprofilServiceMessage {
  id: string;
  header: string;
  label: string;
  date: string;
  time: string;
  target: ("patient" | "hcp")[];
  active: "yes" | "no";
}

export type proTaskStatus =
  | "CANCELLED"
  | "COMPLETED"
  | "ACTIVE"
  | "CLOSE_TO_EXPIRATION"
  | "PLANNED_CONSULTATION_DATE_PASSED";

export const proTaskStatusOptions: {
  id: proTaskStatus;
  i18n: string;
}[] = [
  {
    id: "CANCELLED",
    i18n: _i18n_str("PRO_TASK_STATUS.CANCELLED")
  },
  {
    id: "COMPLETED",
    i18n: _i18n_str("PRO_TASK_STATUS.COMPLETED")
  },
  {
    id: "ACTIVE",
    i18n: _i18n_str("PRO_TASK_STATUS.ACTIVE")
  },
  {
    id: "CLOSE_TO_EXPIRATION",
    i18n: _i18n_str("PRO_TASK_STATUS.CLOSE_TO_EXPIRATION")
  },
  {
    id: "PLANNED_CONSULTATION_DATE_PASSED",
    i18n: _i18n_str("PRO_TASK_STATUS.PLANNED_CONSULTATION_DATE_PASSED")
  }
];

export type proTaskDetailedStatus =
  | "CANCELLED"
  | "COMPLETED_AND_READY_FOR_CONSULTATION"
  | "COMPLETED_WITH_PATIENT"
  | "PARTIALLY_COMPLETED"
  | "PATIENT_FAILED_TO_APPEAR"
  | "ACTIVE"
  | "NOT_COMPLETED_CONSULTATION_IS_SOON"
  | "NOT_COMPLETED_DAY_OF_CONSULTATION"
  | "PLANNED_CONSULTATION_DATE_PASSED";

export const proTaskDetailedStatusOptions: {
  id: proTaskDetailedStatus;
  i18n: string;
}[] = [
  {
    id: "CANCELLED",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.CANCELLED")
  },
  {
    id: "COMPLETED_AND_READY_FOR_CONSULTATION",
    i18n: _i18n_str(
      "PRO_TASK_DETAILED_STATUS.COMPLETED_AND_READY_FOR_CONSULTATION"
    )
  },
  {
    id: "COMPLETED_WITH_PATIENT",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.COMPLETED_WITH_PATIENT")
  },
  {
    id: "PARTIALLY_COMPLETED",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.PARTIALLY_COMPLETED")
  },
  {
    id: "PATIENT_FAILED_TO_APPEAR",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.PATIENT_FAILED_TO_APPEAR")
  },
  {
    id: "ACTIVE",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.ACTIVE")
  },
  {
    id: "NOT_COMPLETED_CONSULTATION_IS_SOON",
    i18n: _i18n_str(
      "PRO_TASK_DETAILED_STATUS.NOT_COMPLETED_CONSULTATION_IS_SOON"
    )
  },
  {
    id: "NOT_COMPLETED_DAY_OF_CONSULTATION",
    i18n: _i18n_str(
      "PRO_TASK_DETAILED_STATUS.NOT_COMPLETED_DAY_OF_CONSULTATION"
    )
  },
  {
    id: "PLANNED_CONSULTATION_DATE_PASSED",
    i18n: _i18n_str("PRO_TASK_DETAILED_STATUS.PLANNED_CONSULTATION_DATE_PASSED")
  }
];

export type twoFactorMethods = "email" | "SMS";

export const twoFactorMethodOptions: {
  id: twoFactorMethods;
  i18n: string;
}[] = [
  {
    id: "SMS",
    i18n: _i18n_str("TWO_FACTOR_METHODS.SMS")
  },
  {
    id: "email",
    i18n: _i18n_str("TWO_FACTOR_METHODS.EMAIL")
  }
];

export const PRO_TASK_CANCELLATION_REASON_SYSTEM_URL =
  "https://diaprofil.zitelab.eu/fhir/pro-task-cancellation-reason";

export type proTaskCancellationReasons =
  | "CREATED_IN_ERROR"
  | "CONSULTATION_POSTPONED"
  | "USE_OF_PRO_REJECTED_BY_PATIENT"
  | "PATIENT_NO_LONGER_FOLLOWED"
  | "OTHER"
  | "TASK_EXPIRED";

export const proTaskCancellationReasonOptions: {
  id: proTaskCancellationReasons;
  i18n: string;
}[] = [
  {
    id: "CREATED_IN_ERROR",
    i18n: _i18n_str("PRO_TASK_CANCELLATION_REASON.CREATED_IN_ERROR")
  },
  {
    id: "CONSULTATION_POSTPONED",
    i18n: _i18n_str("PRO_TASK_CANCELLATION_REASON.CONSULTATION_POSTPONED")
  },
  {
    id: "TASK_EXPIRED",
    i18n: _i18n_str("PRO_TASK_CANCELLATION_REASON.TASK_EXPIRED")
  },
  {
    id: "USE_OF_PRO_REJECTED_BY_PATIENT",
    i18n: _i18n_str(
      "PRO_TASK_CANCELLATION_REASON.USE_OF_PRO_REJECTED_BY_PATIENT"
    )
  },
  {
    id: "PATIENT_NO_LONGER_FOLLOWED",
    i18n: _i18n_str("PRO_TASK_CANCELLATION_REASON.PATIENT_NO_LONGER_FOLLOWED")
  },
  {
    id: "OTHER",
    i18n: _i18n_str("PRO_TASK_CANCELLATION_REASON.OTHER")
  }
];

export type PatientDeactivationReason =
  | "DEACTIVATION_REASON.PATIENT_DOES_NOT_WISH_TO_USE_PRO"
  | "DEACTIVATION_REASON.PATIENT_DOES_NOT_HAVE_NEMID"
  | "DEACTIVATION_REASON.PATIENT_DOES_NOT_SPEAK_DANISH"
  | "DEACTIVATION_REASON.CREATED_IN_ERROR"
  | "DEACTIVATION_REASON.CONSULTATION_POSTPONED_A_LOT"
  | "DEACTIVATION_REASON.PATIENT_DOES_NOT_WANT_TO_BE_FOLLOWED_HERE"
  | "DEACTIVATION_REASON.PATIENT_FINISHED_HERE"
  | "DEACTIVATION_REASON.PATIENT_DID_NOT_SHOW_TWICE"
  | "DEACTIVATION_REASON.OTHER";

export const patientDeactivationReasonOptions: {
  id: PatientDeactivationReason;
  i18n: string;
}[] = [
  {
    id: "DEACTIVATION_REASON.PATIENT_DOES_NOT_WISH_TO_USE_PRO",
    i18n: _i18n_str("DEACTIVATION_REASON.PATIENT_DOES_NOT_WISH_TO_USE_PRO")
  },
  {
    id: "DEACTIVATION_REASON.PATIENT_DOES_NOT_HAVE_NEMID",
    i18n: _i18n_str("DEACTIVATION_REASON.PATIENT_DOES_NOT_HAVE_NEMID")
  },
  {
    id: "DEACTIVATION_REASON.PATIENT_DOES_NOT_SPEAK_DANISH",
    i18n: _i18n_str("DEACTIVATION_REASON.PATIENT_DOES_NOT_SPEAK_DANISH")
  },
  {
    id: "DEACTIVATION_REASON.CREATED_IN_ERROR",
    i18n: _i18n_str("DEACTIVATION_REASON.CREATED_IN_ERROR")
  },
  {
    id: "DEACTIVATION_REASON.CONSULTATION_POSTPONED_A_LOT",
    i18n: _i18n_str("DEACTIVATION_REASON.CONSULTATION_POSTPONED_A_LOT")
  },
  {
    id: "DEACTIVATION_REASON.PATIENT_DOES_NOT_WANT_TO_BE_FOLLOWED_HERE",
    i18n: _i18n_str(
      "DEACTIVATION_REASON.PATIENT_DOES_NOT_WANT_TO_BE_FOLLOWED_HERE"
    )
  },
  {
    id: "DEACTIVATION_REASON.PATIENT_FINISHED_HERE",
    i18n: _i18n_str("DEACTIVATION_REASON.PATIENT_FINISHED_HERE")
  },
  {
    id: "DEACTIVATION_REASON.PATIENT_DID_NOT_SHOW_TWICE",
    i18n: _i18n_str("DEACTIVATION_REASON.PATIENT_DID_NOT_SHOW_TWICE")
  },
  {
    id: "DEACTIVATION_REASON.OTHER",
    i18n: _i18n_str("DEACTIVATION_REASON.OTHER")
  }
];

export interface IClinicalSuiteIncomingParams {
  userUuid?: string;
  ssn?: string;
  patientName?: string;
  encounterStartTime?: string;
  careEventStartTime?: string;
  systemToken?: string;
  diaprofilId?: string;
}

export interface IAdLoginPayload {
  token?: string;
  status?: string | number;
  error?: string;
  error_type?: string;
}

export interface InitEhrResult {
  multiple?: boolean;
  users?: {
    organizationName?: string;
    userId?: string;
  }[];
  cs_user?: {
    home_path: string;
    jwt_token: string;
    organization_uid: string;
  };
  patient?: {
    fhir_id: string;
    fullname: string;
    ssn: string;
  };
  startTime?: string;
  hasError: boolean;
  errorMsg?: string;
}

/**
 * Used to ensure backwards compatibility with pre-1.0.8 versions of questionnaire
 */
export const wishesLegacyConfig: WidgetConfig<any> = {
  name: "wishes",
  widgetType: "RadioSelectionWidget",
  label: "Ønsker til støtte eller opbakning til håndtering af diabetes",
  prefix: "N/A",
  value: null,
  defaultValue: null,
  displayOnly: false,
  summaryMode: false,
  shown: true,
  isShown: () => true,
  disabled: false,
  required: true,
  widgetNumber: 0,
  pageNumber: 0,
  multiple: true,
  validators: [],
  controlType: "FormGroup",
  enableWhen: [],
  enableWhenOperator: null,
  enableWhenFunction: null,
  translations: {},
  childWidgets: null,
  svgGroupItemElement: null,
  isCustomSelectionModalEnabled: false,
  metadata: {},
  setTranslatableProperty: null,
  fhirItem: {
    extension: [
      {
        url: "http://zitelab.eu/fhir/extensions/questionnaireItemDescription",
        valueMarkdown:
          "Nedenfor kan du markere hvis der er områder af din diabetesbehandling, du ønsker at få støtte eller særlig opbakning til de næste 1-3 måneder.\n<br/><br/>\nJeg ønsker støtte til at:"
      },
      {
        url:
          "http://zitelab.eu/fhir/extensions/QuestionnaireItemValueBasedHealthTopic",
        valueString: "WISHES"
      },
      {
        url: "http://zitelab.eu/fhir/extensions/QuestionnaireItemIdInQFDD",
        valueString: "2228.Q"
      },
      {
        url:
          "http://zitelab.eu/fhir/extensions/QuestionnaireItemStatisticsName",
        valueString: "Wishesforsupport"
      },
      {
        url: "http://zitelab.eu/fhir/extensions/translations",
        valueCode: "i18n",
        extension: [
          {
            url: "en-GB",
            extension: [
              {
                url: "text",
                valueString:
                  "Ønsker til støtte eller opbakning til håndtering af diabetes"
              },
              {
                url: "QuestionnaireItemDescription",
                valueString:
                  "Nedenfor kan du markere hvis der er områder af din diabetesbehandling, du ønsker at få støtte eller særlig opbakning til de næste 1-3 måneder.\n<br/><br/>\nJeg ønsker støtte til at:"
              },
              {
                url: "QuestionnaireItemValueBasedHealthTopic",
                valueString: "Ønsker"
              },
              {
                url: "QuestionnaireItemIdInQFDD",
                valueString: "2228.Q"
              },
              {
                url: "QuestionnaireItemStatisticsName",
                valueString: "Wishesforsupport"
              }
            ]
          },
          {
            url: "da-DK",
            extension: [
              {
                url: "text",
                valueString:
                  "Ønsker til støtte eller opbakning til håndtering af diabetes"
              },
              {
                url: "QuestionnaireItemDescription",
                valueString:
                  "Nedenfor kan du markere hvis der er områder af din diabetesbehandling, du ønsker at få støtte eller særlig opbakning til de næste 1-3 måneder.\n<br/><br/>\nJeg ønsker støtte til at:"
              },
              {
                url: "QuestionnaireItemValueBasedHealthTopic",
                valueString: "Ønsker"
              },
              {
                url: "QuestionnaireItemIdInQFDD",
                valueString: "2228.Q"
              },
              {
                url: "QuestionnaireItemStatisticsName",
                valueString: "Wishesforsupport"
              }
            ]
          }
        ]
      }
    ],
    linkId: "wishes",
    prefix: "7.",
    text: "Ønsker til støtte eller opbakning til håndtering af diabetes",
    repeats: true,
    type: "choice",
    options: {
      display: "WISH_OPTIONS",
      reference: "ValueSet/feeaf989-1584-4c8b-82a9-dd4eec8bee72"
    }
  }
};

/**
 * Used to ensure backwards compatibility with pre-1.0.8 versions of questionnaire
 */
export const otherWishesLegacyConfig: WidgetConfig<any> = {
  name: "otherWishes",
  widgetType: "StringWidget",
  label: "Andre ønsker",
  prefix: "N/A",
  value: null,
  defaultValue: null,
  displayOnly: false,
  summaryMode: false,
  shown: true,
  isShown: () => true,
  disabled: false,
  required: true,
  widgetNumber: 0,
  pageNumber: 0,
  multiple: true,
  validators: [],
  controlType: "FormGroup",
  enableWhen: [],
  enableWhenOperator: null,
  enableWhenFunction: null,
  translations: {},
  childWidgets: null,
  svgGroupItemElement: null,
  isCustomSelectionModalEnabled: false,
  metadata: {},
  setTranslatableProperty: null,
  fhirItem: {
    extension: [
      {
        url: "http://zitelab.eu/fhir/extensions/questionnaireItemDescription",
        valueMarkdown: "Her kan du skrive, hvad du ønsker støtte til"
      },
      {
        url:
          "http://zitelab.eu/fhir/extensions/QuestionnaireItemValueBasedHealthTopic",
        valueString: "WISHES"
      },
      {
        url: "http://zitelab.eu/fhir/extensions/QuestionnaireItemIdInQFDD",
        valueString: "2229.Q"
      },
      {
        url:
          "http://zitelab.eu/fhir/extensions/QuestionnaireItemStatisticsName",
        valueString: "otherwishesforsupport"
      },
      {
        url: "http://zitelab.eu/fhir/extensions/translations",
        valueCode: "i18n",
        extension: [
          {
            url: "en-GB",
            extension: [
              {
                url: "text",
                valueString: "Andre ønsker"
              },
              {
                url: "QuestionnaireItemDescription",
                valueString: "Her kan du skrive, hvad du ønsker støtte til"
              },
              {
                url: "QuestionnaireItemValueBasedHealthTopic",
                valueString: "Ønsker"
              },
              {
                url: "QuestionnaireItemIdInQFDD",
                valueString: "2229.Q"
              },
              {
                url: "QuestionnaireItemStatisticsName",
                valueString: "otherwishesforsupport"
              }
            ]
          },
          {
            url: "da-DK",
            extension: [
              {
                url: "text",
                valueString: "Andre ønsker"
              },
              {
                url: "QuestionnaireItemDescription",
                valueString: "Her kan du skrive, hvad du ønsker støtte til"
              },
              {
                url: "QuestionnaireItemValueBasedHealthTopic",
                valueString: "Ønsker"
              },
              {
                url: "QuestionnaireItemIdInQFDD",
                valueString: "2229.Q"
              },
              {
                url: "QuestionnaireItemStatisticsName",
                valueString: "otherwishesforsupport"
              }
            ]
          }
        ]
      }
    ],
    linkId: "otherWishes",
    enableWhen: [
      {
        question: "wishes",
        answerString: "OTHER"
      }
    ],
    prefix: "7.a.",
    text: "Andre ønsker",
    type: "string"
  }
};

export type fetchPeriodType =
  | "TODAY"
  | "FIVE_WORK_DAYS"
  | "TEN_WORK_DAYS"
  | "ALL"
  | "CUSTOM";

export const QNR_RESPONSE_CONSULTATION_STATUS_EXT_URL =
  "https://diaprofil.dk/api/fhir-extensions/questionnaire-response-validated";

export type ConsultationStatus =
  | "COMPLETED_WITH_PATIENT"
  | "PARTIALLY_COMPLETED"
  | "PATIENT_FAILED_TO_APPEAR";



// WEBPACK FOOTER //
// ./src/app/models/types.ts